import { AuthPipe, canActivate, redirectLoggedInTo } from '@angular/fire/auth-guard';
import { Routes } from '@angular/router';
import { SubscriptionPollerComponent } from '@app/domains/billing/feature-processing/components/subscription-poller/subscription-poller';
// eslint-disable-next-line max-len
import { OrganisationSettingsComponent } from '@app/domains/organisations/feature-settings/components/organisation-settings/organisation-settings';
import { UserRole } from '@app/shared/domain';
import { hasExactRole, hasRoleGuard } from '@app/shared/util';
import { PrimeIcons } from 'primeng/api';
import { DashboardComponent } from './dashboard/dashboard';
import { DashboardHomeComponent } from './dashboard/dashboard-home/dashboard-home';
import { StartComponent } from './dashboard/start/start';
import { HomeComponent } from './public/home/home';
import { LoginComponent } from './public/login/login';
import { PrivacyComponent } from './public/privacy/privacy';
import { PublicComponent } from './public/public';
import { TermsComponent } from './public/terms/terms';

const redirectLoggedInToDashboard = (): AuthPipe => redirectLoggedInTo(['']);

const sharedDashboardRoutes: Routes = [
  {
    title: 'Dashboard',
    path: '',
    data: { icon: PrimeIcons.HOME },
    component: DashboardHomeComponent,
  },
  {
    title: 'Settings',
    path: 'settings',
    loadChildren: () => import('../domains/auth/auth-routes').then((m) => m.SETTINGS_ROUTES),
  },
  {
    title: 'Organisation',
    path: 'settings/organisation',
    component: OrganisationSettingsComponent,
    canActivate: [hasExactRole(UserRole.Owner, UserRole.Admin)],
  },
  {
    title: 'Gates',
    path: 'gates',
    data: { icon: PrimeIcons.MAP_MARKER },
    loadComponent: () => import('../domains/gates/feature-list/components/user-gates/user-gates').then((m) => m.UserGatesComponent),
  },
];

export const routes: Routes = [
  {
    path: '',
    component: PublicComponent,
    children: [
      { path: '', component: HomeComponent },
      {
        title: 'Login',
        path: 'login',
        ...canActivate(redirectLoggedInToDashboard),
        component: LoginComponent,
      },
      { path: 'privacy', component: PrivacyComponent },
      { path: 'terms', component: TermsComponent },
    ],
  },
  {
    title: 'Reset Password',
    path: 'reset-password',
    loadComponent: () =>
      import('../domains/auth/feature-reset-password/components/reset-password/reset-password').then((m) => m.ResetPasswordComponent),
  },
  {
    title: 'Account Setup',
    path: 'setup',
    loadComponent: () => import('../domains/auth/feature-setup/components/setup/setup').then((m) => m.SetupComponent),
  },
  {
    title: 'Redirecting...',
    path: 'redirect',
    loadComponent: () => import('../domains/auth/feature-redirect/components/redirect/redirect').then((m) => m.RedirectComponent),
  },
  {
    title: 'Signing In...',
    path: 'login-redirect',
    loadComponent: () =>
      import('../domains/auth/feature-login-redirect/components/login-redirect/login-redirect').then((m) => m.LoginRedirectComponent),
  },
  {
    title: 'Processing Payment...',
    path: 'processing',
    component: SubscriptionPollerComponent,
  },
  {
    title: 'Start',
    path: 'start',
    component: StartComponent,
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    data: { role: UserRole.SuperAdmin },
    canMatch: [hasRoleGuard],
    children: [
      ...sharedDashboardRoutes,
      {
        path: 'admin',
        title: 'Users',
        data: { icon: PrimeIcons.USERS },
        loadComponent: () =>
          import('../domains/organisations/feature-admin/components/admin-dashboard/admin-dashboard').then(
            (m) => m.AdminDashboardComponent,
          ),
      },
      {
        path: 'organisations',
        title: 'Organisations',
        data: { icon: PrimeIcons.BUILDING },
        loadComponent: () =>
          import('../domains/organisations/feature-dashboard/components/organisations-dashboard/organisations-dashboard').then(
            (m) => m.OrganisationsDashboardComponent,
          ),
      },
      {
        path: 'admin/:id',
        title: 'User Detail',
        loadComponent: () =>
          import('../domains/organisations/feature-admin-detail/components/user-detail/user-detail').then((m) => m.UserDetailComponent),
      },
      {
        path: 'organisations/:id',
        title: 'Organisation Detail',
        loadComponent: () =>
          import('../domains/organisations/feature-detail/components/organisation-detail/organisation-detail').then(
            (m) => m.OrganisationDetailComponent,
          ),
      },
      {
        title: 'SMS Commands',
        path: 'settings/sms-commands',
        loadComponent: () =>
          import('../domains/gates/feature-sms-commands/components/sms-commands-settings/sms-commands-settings').then(
            (m) => m.SmsCommandsSettingsComponent,
          ),
      },
      {
        title: 'Server Intercom Numbers',
        path: 'settings/intercom-numbers',
        loadComponent: () =>
          import('../domains/gates/feature-intercom-numbers/components/intercom-numbers-settings/intercom-numbers-settings').then(
            (m) => m.IntercomNumbersSettingsComponent,
          ),
      },
      {
        path: 'gate/new',
        loadComponent: () => import('../domains/gates/feature-edit/components/update-gate/update-gate').then((m) => m.UpdateGateComponent),
      },
      {
        path: 'gate/:id/update',
        loadComponent: () => import('../domains/gates/feature-edit/components/update-gate/update-gate').then((m) => m.UpdateGateComponent),
      },
      {
        title: 'Gate Events',
        path: 'gate/:gateId/events',
        loadComponent: () =>
          import('../domains/gates/feature-events/components/gate-events/gate-events').then((m) => m.GateEventsComponent),
      },
      {
        title: 'Access Windows',
        path: 'gate/:gateId/access-windows',
        loadComponent: () =>
          import('../domains/gates/feature-access-windows/components/access-windows/access-windows').then((m) => m.AccessWindowsComponent),
      },
      {
        path: 'gate/:id',
        loadComponent: () =>
          import('../domains/gates/feature-control/components/control-panel/control-panel').then((m) => m.ControlPanelComponent),
      },
    ],
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    data: { role: UserRole.Owner },
    canMatch: [hasRoleGuard],
    children: [
      ...sharedDashboardRoutes,
      {
        title: 'Plans',
        path: 'plans',
        loadComponent: () => import('../domains/billing/feature-plans/components/plans/plans').then((m) => m.PlansComponent),
      },
      {
        title: 'Billing',
        path: 'settings/billing',
        canActivate: [hasExactRole(UserRole.Owner)],
        loadComponent: () =>
          import('../domains/billing/feature-billing/components/billing-settings/billing-settings').then((m) => m.BillingSettingsComponent),
      },
      {
        data: { icon: PrimeIcons.USERS },
        title: 'Contacts',
        path: 'contacts',
        loadComponent: () => import('../domains/contacts/feature-list/components/contacts/contacts').then((m) => m.ContactsComponent),
      },
      {
        title: 'Server Intercom Numbers',
        path: 'settings/intercom-numbers',
        loadComponent: () =>
          import('../domains/gates/feature-intercom-numbers/components/intercom-numbers-settings/intercom-numbers-settings').then(
            (m) => m.IntercomNumbersSettingsComponent,
          ),
      },
      {
        path: 'gate/new',
        loadComponent: () => import('../domains/gates/feature-edit/components/update-gate/update-gate').then((m) => m.UpdateGateComponent),
      },
      {
        path: 'gate/:id/update',
        loadComponent: () => import('../domains/gates/feature-edit/components/update-gate/update-gate').then((m) => m.UpdateGateComponent),
      },
      {
        title: 'Gate Events',
        path: 'gate/:gateId/events',
        loadComponent: () =>
          import('../domains/gates/feature-events/components/gate-events/gate-events').then((m) => m.GateEventsComponent),
      },
      {
        title: 'Access Windows',
        path: 'gate/:gateId/access-windows',
        loadComponent: () =>
          import('../domains/gates/feature-access-windows/components/access-windows/access-windows').then((m) => m.AccessWindowsComponent),
      },
      {
        path: 'gate/:id',
        loadComponent: () =>
          import('../domains/gates/feature-control/components/control-panel/control-panel').then((m) => m.ControlPanelComponent),
      },
    ],
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    data: { role: UserRole.Admin },
    canMatch: [hasRoleGuard],
    children: [
      ...sharedDashboardRoutes,
      {
        title: 'Plans',
        path: 'plans',
        loadComponent: () => import('../domains/billing/feature-plans/components/plans/plans').then((m) => m.PlansComponent),
      },
      {
        data: { icon: PrimeIcons.USERS },
        title: 'Contacts',
        path: 'contacts',
        loadComponent: () => import('../domains/contacts/feature-list/components/contacts/contacts').then((m) => m.ContactsComponent),
      },
      {
        title: 'Server Intercom Numbers',
        path: 'settings/intercom-numbers',
        loadComponent: () =>
          import('../domains/gates/feature-intercom-numbers/components/intercom-numbers-settings/intercom-numbers-settings').then(
            (m) => m.IntercomNumbersSettingsComponent,
          ),
      },
      {
        path: 'gate/new',
        loadComponent: () => import('../domains/gates/feature-edit/components/update-gate/update-gate').then((m) => m.UpdateGateComponent),
      },
      {
        path: 'gate/:id/update',
        loadComponent: () => import('../domains/gates/feature-edit/components/update-gate/update-gate').then((m) => m.UpdateGateComponent),
      },
      {
        title: 'Gate Events',
        path: 'gate/:gateId/events',
        loadComponent: () =>
          import('../domains/gates/feature-events/components/gate-events/gate-events').then((m) => m.GateEventsComponent),
      },
      {
        title: 'Access Windows',
        path: 'gate/:gateId/access-windows',
        loadComponent: () =>
          import('../domains/gates/feature-access-windows/components/access-windows/access-windows').then((m) => m.AccessWindowsComponent),
      },
      {
        path: 'gate/:id',
        loadComponent: () =>
          import('../domains/gates/feature-control/components/control-panel/control-panel').then((m) => m.ControlPanelComponent),
      },
    ],
  },
  {
    path: 'dashboard',
    component: DashboardComponent,
    data: { role: UserRole.Collaborator },
    canMatch: [hasRoleGuard],
    children: [
      ...sharedDashboardRoutes,
      {
        title: 'Plans',
        path: 'plans',
        loadComponent: () => import('../domains/billing/feature-plans/components/plans/plans').then((m) => m.PlansComponent),
      },
      {
        data: { icon: PrimeIcons.USERS },
        title: 'Contacts',
        path: 'contacts',
        loadComponent: () => import('../domains/contacts/feature-list/components/contacts/contacts').then((m) => m.ContactsComponent),
      },
      {
        title: 'Gate Events',
        path: 'gate/:gateId/events',
        loadComponent: () =>
          import('../domains/gates/feature-events/components/gate-events/gate-events').then((m) => m.GateEventsComponent),
      },
      {
        path: 'gate/:id',
        loadComponent: () =>
          import('../domains/gates/feature-control/components/control-panel/control-panel').then((m) => m.ControlPanelComponent),
      },
    ],
  },
  {
    path: '**',
    redirectTo: '',
  },
];
